/**
 * 팝업 광고 목록 페이지
 * - 앱 진입 시 노출되는 팝업 관리 (이미지 고정 크기: 340x620)
 * - 노출 중인 팝업 중 랜덤 1개가 앱에 노출됨
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useState } from "react";
import { popupService } from "../../services/popupService";
import { Popup } from "../../types/popup";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import { formatDate } from "../../utils/format";

function PopupCard({ popup, onDelete }: { popup: Popup; onDelete: (id: number) => void }) {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group">
      {/* 이미지 - 340:620 비율 고정 */}
      <Link to={`/admin/popup/${popup.id}`} state={{ popup }}>
        <div className="relative w-full" style={{ aspectRatio: "340/620" }}>
          <img
            src={popup.imageUrl}
            alt={popup.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.style.display = "none";
            }}
          />
          {!popup.isVisible && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <span className="px-4 py-2 bg-gray-800 text-white text-sm font-bold rounded-full">
                <i className="fas fa-pause-circle mr-2"></i>비활성
              </span>
            </div>
          )}
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 text-xs font-bold rounded-full ${
              popup.isVisible
                ? "bg-green-500 text-white"
                : "bg-gray-400 text-white"
            }`}>
              {popup.isVisible ? "노출 중" : "숨김"}
            </span>
          </div>
        </div>
      </Link>

      <div className="p-4">
        <Link to={`/admin/popup/${popup.id}`} state={{ popup }}>
          <h3 className="text-base font-bold text-gray-800 mb-1 group-hover:text-primary transition-colors truncate">
            {popup.title}
          </h3>
        </Link>
        {popup.linkUrl && (
          <p className="text-xs text-gray-400 mb-3 truncate">
            <i className="fas fa-link mr-1"></i>{popup.linkUrl}
          </p>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{formatDate(popup.createdAt)}</span>
          <div className="flex gap-2">
            <Link
              to={`/admin/popup/${popup.id}`}
              state={{ popup }}
              className="px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            >
              수정
            </Link>
            <button
              onClick={() => onDelete(popup.id)}
              className="px-3 py-1 text-xs bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors"
            >
              삭제
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PopupList() {
  const queryClient = useQueryClient();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data: popups = [], isLoading, error } = useQuery({
    queryKey: ["popups"],
    queryFn: popupService.getAll,
  });

  const deleteMutation = useMutation({
    mutationFn: popupService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["popups"] });
      setDeleteConfirmId(null);
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="팝업 목록을 불러오지 못했습니다." />;

  const visibleCount = popups.filter((p) => p.isVisible).length;

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">팝업 관리</h1>
          <p className="text-sm text-gray-500 mt-1">
            앱 진입 시 노출되는 팝업 광고 관리 · 노출 중인 팝업 중 <strong>랜덤 1개</strong> 노출
          </p>
        </div>
        <Link
          to="/admin/popup/new"
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-semibold hover:opacity-90 transition-opacity"
        >
          <i className="fas fa-plus"></i>
          팝업 등록
        </Link>
      </div>

      {/* 통계 */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">전체 팝업</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{popups.length}개</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">노출 중</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{visibleCount}개</p>
        </div>
      </div>

      {/* 안내 */}
      {visibleCount > 1 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <i className="fas fa-info-circle mr-2"></i>
          노출 중인 팝업이 {visibleCount}개입니다. 앱 진입 시 그 중 <strong>랜덤 1개</strong>가 표시됩니다.
        </div>
      )}

      {/* 팝업 그리드 */}
      {popups.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <i className="fas fa-window-restore text-5xl mb-4 block"></i>
          <p className="text-lg">등록된 팝업이 없습니다.</p>
          <Link to="/admin/popup/new" className="mt-4 inline-block text-primary underline text-sm">
            첫 팝업 등록하기
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {popups.map((popup) => (
            <PopupCard
              key={popup.id}
              popup={popup}
              onDelete={(id) => setDeleteConfirmId(id)}
            />
          ))}
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">팝업 삭제</h3>
            <p className="text-sm text-gray-600 mb-6">삭제한 팝업은 복구할 수 없습니다. 삭제하시겠습니까?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "삭제 중..." : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
